import { ContextType, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useTheme } from 'styled-components'
import { noop } from 'lodash-es'

import { ExpectedAsset } from '@/feature/assets'
import { useGetUser } from '@/feature/auth'
import { useFlag } from '@/feature/feature-flags'
import { ActiveAssetContext, AssetMarker, ShareContactDetails } from '@/feature/observatory'
import { AssetPanel } from '@/feature/observatory/components/AssetPanel/AssetPanel'
import { MapHandler } from '@/feature/map'
import { FormattedMessage } from 'react-intl'
import { LuChevronLeft, LuFilePen, LuMapPinOff, LuShare, LuTrash2 } from 'react-icons/lu'
import { LayerManager } from '@/feature/map/components/LayerManager'
import { MapIconLegend } from '@/feature/map/components/MapIconLegend'
import { useAssetTelemetry } from '@/feature/telemetry'

import { Nullable } from '@/lib/core/interfaces/common.types'
import { Button, IconButton } from '@/lib/core/components/Button'
import { CircleAvatar } from '@/lib/core/components/CircleAvatar'
import { Loader } from '@/lib/core/components/Loader'
import { LoadingWrapper } from '@/lib/core/components/Table/Table.styles'
import { Truncate } from '@/lib/core/components/Truncate'
import { Tooltip } from '@/lib/core/components/Tooltip'

import {
  AssetViewActionButton,
  AssetViewActions,
  AssetViewAlert,
  AssetViewAlertHeading,
  AssetViewAvatar,
  AssetViewContent,
  AssetViewDetailsWrapper,
  AssetViewFooter,
  AssetViewFormWrapper,
  AssetViewHeader,
  AssetViewMapWrapper,
  AssetViewSharedBox,
  AssetViewTitle,
  AssetViewWrapper,
  DataCell,
  FormBox,
  LabelCell,
  LocationPausedAlert,
  LocationPausedText,
  LocationPausedWrapper,
  SharedBoxTable,
} from '@/lib/features/Assets/components/AssetView/AssetView.styles'
import { Asset, AssetContentType, BridgeStatus } from '@/lib/features/Assets/interfaces'
import { AssetViewForm } from '@/lib/features/Assets/components/AssetView/AssetViewForm'
import assetViewMessages from '@/lib/features/Assets/components/AssetView/assetView.messages'
import { Share } from '@/lib/features/SmartShares/interfaces'

import { formatAsDuration, formatDate } from '@/lib/utils/date-time'

export type AssetViewProps = {
  asset: Nullable<Asset>
  expectedAsset: Nullable<ExpectedAsset>
  isLoading: boolean
  onClose: (withUpdate?: boolean) => void
  onDelete?: (type: AssetContentType, id: Asset['objectId']) => void
  onShare?: (asset: Asset) => void
}
const defaultPosition = { center: { lat: -30.069547984853443, lng: 24.27277106134315 } }

const AssetMapView = ({
  asset,
  shouldDisplayMarker,
}: {
  asset: Nullable<Asset>
  shouldDisplayMarker: boolean
}) => {
  const hideMarker = !shouldDisplayMarker || !asset

  const mapPosition = useFlag('mapDefaultPosition', defaultPosition)

  // https://react.dev/reference/react/useContext#caveats
  const activeAssetValues = useMemo(() => {
    return {
      activeAsset: asset || undefined,
      setActiveAsset: noop,
      clearActiveAsset: noop,
    } satisfies ContextType<typeof ActiveAssetContext>
  }, [asset])

  return (
    <ActiveAssetContext.Provider value={activeAssetValues}>
      <MapHandler
        center={mapPosition.center}
        zoom={14}
        mapOptions={{
          clickableIcons: false,
          mapTypeControl: false,
          mapTypeControlOptions: {
            mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
            get position() {
              return window.google?.maps.ControlPosition?.LEFT_BOTTOM
            },
            get style() {
              return window.google?.maps.MapTypeControlStyle?.HORIZONTAL_BAR
            },
          },
          streetViewControl: false,
          zoomControl: true,
          isFractionalZoomEnabled: true,
        }}
      >
        {hideMarker ? null : <AssetMarker asset={asset} />}
        <LayerManager
          toggles={{
            traffic: true,
            satellite: true,
          }}
        />
        <MapIconLegend />
      </MapHandler>
      <AssetPanel />
    </ActiveAssetContext.Provider>
  )
}

export const AssetView = ({
  asset,
  expectedAsset,
  isLoading,
  onClose,
  onDelete = () => null,
  onShare = () => null,
}: AssetViewProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isUpdated, setIsUpdated] = useState(false)
  const [wrapperOffsetTop, setWrapperOffsetTop] = useState(0)
  const { colors } = useTheme()
  const wrapperRef = useRef<HTMLElement>(null)
  const { data: user } = useGetUser()
  const isAssetShared = useMemo(() => asset?.bridgeStatus !== null, [asset])
  const isRealOwner = useMemo(() => {
    const realOwnerId = expectedAsset?.bridgeId.split('/')[0]

    return user?.organization?.id === Number(realOwnerId)
  }, [expectedAsset])

  const headingMessage = useMemo(() => {
    switch (asset?.contentType) {
      case AssetContentType.PTU:
        return assetViewMessages.ptu
      case AssetContentType.VEHICLE:
      default:
        return assetViewMessages.vehicle
    }
  }, [asset?.contentType])

  const shouldDisplayMap = isRealOwner || asset?.mapIsActive

  const [selectedShare, setSelectedShare] = useState<Share>()

  const shouldDisplayMarker = useMemo(() => {
    if (isRealOwner) {
      return true
    }

    if (asset?.mapIsActive) {
      if (asset.bridgeStatus === BridgeStatus.BORROWED) {
        return dayjs().isBetween(asset.bridgeDateFrom, asset.bridgeDateTo, 'day', '[]')
      }

      return true
    }

    return false
  }, [isRealOwner, asset])

  useEffect(() => {
    if (wrapperOffsetTop === 0 && !isLoading) {
      setWrapperOffsetTop(wrapperRef.current?.getBoundingClientRect().top ?? 0)
    }
  }, [wrapperRef.current?.offsetTop, isLoading])

  const assetTrackingData = useAssetTelemetry(asset?.vehicleBridgeId)

  const renderEditButton = () => {
    if (!isAssetShared) {
      return (
        <Button
          variant="text"
          leftIcon={<LuFilePen size="16" strokeWidth={1.5} />}
          $fitToContent
          onClick={() => {
            setIsEditing(true)
            setIsUpdated(false)
          }}
        >
          <FormattedMessage {...assetViewMessages.edit} />
        </Button>
      )
    }

    return (
      <Tooltip
        trigger="mouseenter"
        placement="bottom"
        tooltipContent={<FormattedMessage {...assetViewMessages.editDisabled} />}
        contentPadding={2}
      >
        <IconButton
          style={{
            display: 'flex',
            gap: 8,
            fontSize: '0.8rem',
            alignItems: 'center',
            width: 'fit-content',
            padding: '8px 16px',
            color: '#91979D',
          }}
          type="button"
          $variant="secondary"
        >
          <LuFilePen size="16" color="#91979D" strokeWidth={1.5} />
          <FormattedMessage {...assetViewMessages.edit} />
        </IconButton>
      </Tooltip>
    )
  }

  const renderDeleteButton = (asset: Asset) => {
    if (!isAssetShared) {
      return (
        <AssetViewActionButton
          variant="text"
          size="large"
          leftIcon={<LuTrash2 size="16" strokeWidth={1.5} />}
          $fitToContent
          onClick={() => onDelete(asset.contentType, asset.objectId)}
        />
      )
    }

    return (
      <Tooltip
        trigger="mouseenter"
        tooltipContent={<FormattedMessage {...assetViewMessages.deleteDisabled} />}
        contentPadding={2}
      >
        <IconButton style={{ padding: 8, paddingBottom: 12, color: '#91979D' }} type="button">
          <LuTrash2 size="16" strokeWidth={1.5} />
        </IconButton>
      </Tooltip>
    )
  }

  const onChangeShare = (share: Share) => {
    setSelectedShare(share)
  }

  const viewDetails = useMemo(() => {
    if (asset?.bridgeStatus === 'borrowed') {
      return {
        dateFrom: asset.bridgeDateFrom,
        dateTo: asset.bridgeDateTo,
        reference: asset.reference,
      }
    }
    return {
      dateFrom: selectedShare?.dateFrom,
      dateTo: selectedShare?.dateTo,
      reference: selectedShare?.reference,
    }
  }, [asset, selectedShare])

  if (isLoading) {
    return (
      <LoadingWrapper>
        <Loader color={colors.backgroundTab} />
      </LoadingWrapper>
    )
  }

  return (
    <AssetViewWrapper ref={wrapperRef} $topGap={wrapperOffsetTop} $isLoading={isLoading}>
      <AssetViewDetailsWrapper>
        <AssetViewHeader>
          <Button
            variant="ghost"
            onClick={() => onClose(isUpdated)}
            $fitToContent
            leftIcon={<LuChevronLeft size="16" strokeWidth={1.5} />}
          >
            <FormattedMessage {...assetViewMessages.back} />
          </Button>
          <AssetViewTitle>
            <FormattedMessage {...headingMessage} />
            {': '}
            <Truncate content={asset?.description} limit={40} />
          </AssetViewTitle>
          {isRealOwner && !isEditing && renderEditButton()}
          <AssetViewAvatar>
            <CircleAvatar src={asset?.owner?.avatar} withBorder size={40} />
          </AssetViewAvatar>
        </AssetViewHeader>
        <AssetViewContent>
          <AssetViewFormWrapper>
            {asset ? (
              <>
                {!isRealOwner && asset?.mapIsActive === false && (
                  <LocationPausedAlert>
                    <AssetViewAlertHeading>
                      <FormattedMessage
                        {...assetViewMessages.trackingPausedHeading}
                        values={{ assetId: asset?.bridgeId }}
                      />
                    </AssetViewAlertHeading>
                    <FormattedMessage {...assetViewMessages.trackingPausedContent} />
                  </LocationPausedAlert>
                )}
                {!isEditing && isUpdated && (
                  <FormBox variant="info">
                    <FormattedMessage {...assetViewMessages.assetUpdated} />
                  </FormBox>
                )}
                {isAssetShared && !isEditing && (
                  <AssetViewSharedBox>
                    <SharedBoxTable>
                      <tbody>
                        <tr>
                          <LabelCell>
                            <FormattedMessage {...assetViewMessages.shareDuration} />:
                          </LabelCell>
                          <DataCell>
                            {viewDetails?.dateFrom && formatDate(viewDetails.dateFrom)} -{' '}
                            {viewDetails?.dateTo && formatDate(viewDetails.dateTo)}
                          </DataCell>
                        </tr>
                        <tr>
                          <LabelCell>
                            <FormattedMessage {...assetViewMessages.remaining} />:
                          </LabelCell>
                          <DataCell>{formatAsDuration(viewDetails?.dateTo)}</DataCell>
                        </tr>

                        <tr>
                          <LabelCell>
                            <FormattedMessage {...assetViewMessages.reference} />
                          </LabelCell>
                          <DataCell>{viewDetails?.reference ?? '-'}</DataCell>
                        </tr>

                        <tr>
                          <LabelCell>
                            <FormattedMessage {...assetViewMessages.lastSeen} />
                          </LabelCell>
                          <DataCell>{formatAsDuration(assetTrackingData?.sensorDatetime)}</DataCell>
                        </tr>
                      </tbody>
                    </SharedBoxTable>
                    <ShareContactDetails
                      onChangeShare={onChangeShare}
                      assetId={asset.id}
                      textColor={'#404040'}
                    />

                    <FormattedMessage {...assetViewMessages.durationExpiration} tagName="p" />
                  </AssetViewSharedBox>
                )}
                <AssetViewForm
                  key={asset.id}
                  isRealOwner={isRealOwner}
                  isEditing={isEditing}
                  onCancel={() => setIsEditing(false)}
                  onUpdate={() => {
                    setIsEditing(false)
                    setIsUpdated(true)
                  }}
                  defaultValues={asset}
                  isAssetShared={isAssetShared}
                />
              </>
            ) : (
              <AssetViewAlert>
                <AssetViewAlertHeading>
                  <FormattedMessage
                    {...assetViewMessages.noLongerAvailableHeading}
                    values={{ assetId: expectedAsset?.bridgeId }}
                  />
                </AssetViewAlertHeading>
                <FormattedMessage {...assetViewMessages.noLongerAvailableContent} />
              </AssetViewAlert>
            )}
          </AssetViewFormWrapper>
        </AssetViewContent>
        {!!asset && !isEditing && (
          <AssetViewFooter>
            <AssetViewActions>
              <AssetViewActionButton
                variant="text"
                size="large"
                leftIcon={<LuShare size="16" strokeWidth={1.5} />}
                $fitToContent
                onClick={() => onShare(asset)}
              />
              {asset?.bridgeStatus !== BridgeStatus.BORROWED && renderDeleteButton(asset)}
            </AssetViewActions>
          </AssetViewFooter>
        )}
      </AssetViewDetailsWrapper>
      <AssetViewMapWrapper>
        {shouldDisplayMap ? (
          <AssetMapView asset={asset} shouldDisplayMarker={shouldDisplayMarker} />
        ) : (
          <LocationPausedWrapper>
            <LocationPausedText>
              <FormattedMessage {...assetViewMessages.locationNotAvailable} />
            </LocationPausedText>
            <LuMapPinOff size="56" strokeWidth={1.5} />
          </LocationPausedWrapper>
        )}
      </AssetViewMapWrapper>
    </AssetViewWrapper>
  )
}
